import { useCallback, useLayoutEffect, useMemo } from "react";
import {
  MIN_BAR_SIZE,
  MIN_BAR_SPACING,
  MIN_BAR_WIDTH,
  Props,
} from "./UseWaveform.Parts";

export const useWaveform = ({
  canvasRef,
  waveform,
  width,
  height,
  currentTime,
  totalDuration,
  isHovering,
}: Props) => {
  const { data, length: numOriginalPeaks } = waveform;

  const drawingMetrics = useMemo(() => {
    // Determine how many waveform peaks to average into a single drawn bar.
    const numDrawnBars = Math.floor(width / MIN_BAR_SIZE);
    const peaksPerDrawnBar =
      numOriginalPeaks > numDrawnBars
        ? Math.ceil(numOriginalPeaks / numDrawnBars)
        : 1;
    const effectiveDrawnBars = Math.ceil(numOriginalPeaks / peaksPerDrawnBar);

    // Calculate the final width and spacing of each bar to ensure they fill the canvas.
    let barWidth = MIN_BAR_WIDTH;
    let barSpacing = MIN_BAR_SPACING;
    if (effectiveDrawnBars > 0 && effectiveDrawnBars < width) {
      const spacePerUnit = width / effectiveDrawnBars;
      // Allocate 70% of the space to the bar and 30% to the spacing.
      barWidth = spacePerUnit * 0.7;
      barSpacing = spacePerUnit * 0.3;
    }
    return {
      peaksPerDrawnBar,
      effectiveDrawnBars,
      barWidth,
      barSpacing,
    };
  }, [width, numOriginalPeaks]);

  // The main drawing function, wrapped in useCallback for performance.
  const draw = useCallback(() => {
    if (width === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");

    if (!canvas || !ctx) return;

    const computedStyle = getComputedStyle(canvas);
    const playedColor = computedStyle
      .getPropertyValue("--waveform-played-color")
      .trim();
    const unplayedColor = computedStyle
      .getPropertyValue("--waveform-unplayed-color")
      .trim();
    const hoverColor = computedStyle
      .getPropertyValue("--waveform-hover-color")
      .trim();

    // Adjust canvas resolution for high-DPR (Retina) displays to ensure crisp visuals.
    const dpr = window.devicePixelRatio || 1;
    // Resize the canvas's drawing surface if it doesn't match the desired resolution.
    if (canvas.width !== width * dpr) {
      canvas.width = width * dpr;
    }
    if (canvas.height !== height * dpr) {
      canvas.height = height * dpr;
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    // Scale the context to draw at the correct size.
    ctx.scale(dpr, dpr);

    const { effectiveDrawnBars, peaksPerDrawnBar, barWidth, barSpacing } =
      drawingMetrics;
    const MAX_AMPLITUDE = 127; // The peak data is 8-bit, from -127 to 127.
    const center = height / 2;
    // Calculate the pixel position of the playhead.
    const playheadX =
      totalDuration > 0 ? (currentTime / totalDuration) * width : 0;

    const drawBar = (i: number, startPeak: number, endPeak: number) => {
      // Find the absolute min and max peak values within the averaged segment.
      let segmentLow = MAX_AMPLITUDE;
      let segmentHigh = -MAX_AMPLITUDE;
      for (let j = startPeak; j < endPeak; j++) {
        segmentLow = Math.min(segmentLow, data[2 * j]);
        segmentHigh = Math.max(segmentHigh, data[2 * j + 1]);
      }

      const x = i * (barWidth + barSpacing);
      if (x >= width) return;

      // Normalize the peak values to a 0-1 range and calculate the bar's vertical position.
      const normalizedLow = segmentLow / MAX_AMPLITUDE;
      const normalizedHigh = segmentHigh / MAX_AMPLITUDE;
      const yTop = center + normalizedLow * center;
      const yBottom = center + normalizedHigh * center;
      const drawHeight = yBottom - yTop;

      // Draw the rectangle for the bar.
      ctx.fillRect(x, yTop, barWidth, Math.max(1, drawHeight));
    };

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Draw background waveform
    ctx.fillStyle = isHovering ? hoverColor : unplayedColor;
    for (let i = 0; i < effectiveDrawnBars; i++) {
      const startPeakIndex = i * peaksPerDrawnBar;
      const endPeakIndex = Math.min(
        startPeakIndex + peaksPerDrawnBar,
        numOriginalPeaks,
      );
      drawBar(i, startPeakIndex, endPeakIndex);
    }

    // Draw foreground waveform
    ctx.fillStyle = playedColor;
    for (let i = 0; i < effectiveDrawnBars; i++) {
      const x = i * (barWidth + barSpacing);
      if (x >= playheadX) break;

      const startPeakIndex = i * peaksPerDrawnBar;
      const endPeakIndex = Math.min(
        startPeakIndex + peaksPerDrawnBar,
        numOriginalPeaks,
      );
      drawBar(i, startPeakIndex, endPeakIndex);
    }
  }, [
    canvasRef,
    data,
    drawingMetrics,
    numOriginalPeaks,
    width,
    height,
    currentTime,
    totalDuration,
    isHovering,
  ]);

  useLayoutEffect(() => {
    if (width > 0 && totalDuration > 0) {
      draw();
    }
  }, [draw, width, totalDuration, isHovering, currentTime]);
};
